/**
 * THE SHORT VERSION — one consolidated reading of a player's game.
 *
 * My Performance answers "how am I playing" in several separate blocks, each
 * scrupulous about its own units and its own sample, and between them they
 * never said the thing a player opened the page for. So the page states its
 * conclusion first and shows the evidence after, and this module is the
 * conclusion.
 *
 * ONE FRAME. Every sentence here compares the player with a TYPICAL PLAYER OF
 * THEIR HANDICAP, and nothing else. The first version also carried a line from
 * the stroke accounting — "11.2 of your shots over par go on your long game" —
 * which measures against PAR, where every mid-handicapper's biggest number is
 * before the green. Beside a headline calling that same long game a strength,
 * the two read as a contradiction. Both were true; they were answers to two
 * different questions, and a verdict can only answer one. The accounting stays
 * on the page, below, labelled as what it is.
 *
 * EVERY SENTENCE NAMES ITS SUBJECT. "Better" and "worse", never "ahead",
 * "further from" or "up": a player ahead in two departments and behind in two
 * is both close to and far from a typical player at once, and a golf score
 * that is "up" is a bad one. And the department on the fix card's label is the
 * department its every sentence is about.
 *
 * It invents nothing. Each figure comes through the helpers the evidence
 * blocks render with, so the verdict and the table it summarises cannot quote
 * different numbers.
 */
import {
  benchmarkRows, standingSentence, weakestStanding, decisionFor, trendLabel,
  puttingIsSymptom, onGreenFacts, showNum, showPct, nameList, LIE_WORDS,
} from './shotInsights';

export const PAR_LABEL = { 3: 'Par 3s', 4: 'Par 4s', 5: 'Par 5s' };

/**
 * Which way the scores are going, in words that cannot be read backwards.
 *
 * Lower is better, so the server's 'improving' is a FALLING series. "Scoring
 * up 1.0" was the old wording, and in a golf app "up" is ambiguous — a rising
 * score is a worse one, but "up" is the word for good news everywhere else.
 */
export const scoringTrend = (trend) => {
  if (!trend || !trend.direction) return null;
  if (trend.direction === 'steady') return 'Your recent scores are holding steady';
  const size = Math.abs(trend.delta).toFixed(1);
  const way = trend.direction === 'improving' ? 'better' : 'worse';
  return `Your recent scores are ${size} shots a round ${way} than the ones before`;
};

/** The handicap's own trend. "Down" IS unambiguous for a handicap. */
export const handicapTrend = (trend) => {
  if (!trend || !trend.direction) return null;
  if (trend.direction === 'steady') return 'Handicap holding steady';
  const size = Math.abs(trend.delta).toFixed(1);
  return trend.direction === 'improving'
    ? `Handicap down ${size} on the rounds before`
    : `Handicap up ${size} on the rounds before`;
};

const LONG_GAME = ['approach', 'driving'];
const AROUND_THE_GREEN = ['putting', 'short_game'];

/**
 * The headline. Where the split is the one golfers already narrate their game
 * in — getting to the green against what happens around it — it says that, in
 * those words; otherwise it names the departments.
 *
 * Only on a clean split, with all four compared: "your long game is better"
 * from driving alone would be claiming approach on its behalf.
 */
export const verdictHeadline = (rows, band) => {
  const comparable = (rows || []).filter(r => r.gap);
  if (!comparable.length) return null;
  const keys = (pick) => comparable.filter(pick).map(r => r.key).sort().join();
  const better = keys(r => !r.gap.behind && !r.gap.level);
  const worse = keys(r => r.gap.behind);
  const frame = `Compared with a typical ${band} handicapper`;
  if (better === LONG_GAME.join() && worse === AROUND_THE_GREEN.join()) {
    return `${frame}, your long game is better and your game around the green is worse.`;
  }
  if (better === AROUND_THE_GREEN.join() && worse === LONG_GAME.join()) {
    return `${frame}, your game around the green is better and your long game is worse.`;
  }
  return standingSentence(rows, band);
};

/**
 * The fix card's first sentence: the figure that earns the label, in the unit
 * and with the numbers the table beneath prints for that department.
 */
const FACT = {
  driving: (r, who) => `You hit ${showPct(r.mine)} of fairways; ${who} hits ${showPct(r.mark)}.`,
  approach: (r, who) => `You hit ${showPct(r.mine)} of greens in regulation; ${who} hits ${showPct(r.mark)}.`,
  short_game: (r, who) => `You get up and down ${showPct(r.mine)} of the time; ${who} does it ${showPct(r.mark)} of the time.`,
  putting: (r, who) => `You take ${showNum(r.mine, 1)} putts a round; ${who} takes ${showNum(r.mark, 1)}.`,
};

/**
 * What to do about the short game, in the card's own currency.
 *
 * Not `decisionFor('short_game')`: that quotes a NET save rate by lie, which
 * is the right figure on the Short tab and the wrong one here, beside a gross
 * up-and-down rate — two percentages for one skill on one card. This names
 * the lie and quotes nothing.
 */
const shortGameAction = (shots) => {
  const lies = (shots?.short_game?.net_scramble_by_lie || []).filter(z => z.holes >= 5);
  if (lies.length < 2) return 'Practise chipping and putting from just off the green.';
  const worst = [...lies].sort((a, b) => a.net_scramble_pct - b.net_scramble_pct)[0];
  return `Start from ${LIE_WORDS[worst.lie] || worst.lie} — it is your weakest lie around the green.`;
};

/**
 * The one thing to fix, as three plain sentences about ONE department.
 *
 *   fact    the figure that earns the label
 *   link    present only when another department's shortfall comes from this
 *           one — so the card explains it rather than leaving it unanswered
 *   action  what to go and do
 *
 * A high putting TOTAL with no on-green cause is a symptom, not a cause
 * (`puttingIsSymptom`), so it is left out of the ranking. That is the case the
 * screen got wrong twice: putting ranked worst on its total, and the card then
 * had to explain that the putting stroke was fine — first by saying "leave it
 * alone" under a label reading PUTTING, then by pointing at "the shot that
 * gets you on", which reads as the approach.
 */
export const fixFirst = (shots) => {
  const band = shots?.benchmark?.band;
  const symptom = puttingIsSymptom(shots);
  const worst = weakestStanding(shots, { exclude: symptom ? ['putting'] : [] });
  if (!worst) return null;

  const who = `a typical ${band} handicapper`;
  const link = symptom && worst.key === 'short_game'
    ? `It is also where most of your extra putts come from — ${onGreenFacts(shots.putting)}, so your putting stroke is not the problem.`
    : null;
  const action = worst.key === 'short_game'
    ? shortGameAction(shots)
    : decisionFor(worst.key, shots, true);

  return {
    key: worst.key,
    label: worst.label,
    tone: worst.tone,
    fact: FACT[worst.key](worst, who),
    link,
    action,
  };
};

/**
 * What the verdict is NOT standing on.
 *
 * A consolidated statement is the most confident thing on the screen, so it
 * carries its own gaps. A department below its sample floor is named here
 * rather than silently left out of the headline — otherwise "your driving and
 * approach are better" reads as a four-department verdict when it was a
 * two-department one.
 */
export const caveatSentence = (shots) => {
  if (!shots?.benchmark) return null;
  const quiet = benchmarkRows(shots).filter(r => !r.enough).map(r => r.label.toLowerCase());
  if (!quiet.length) return null;
  return `Nothing is claimed about your ${nameList(quiet)} yet — `
    + `${quiet.length === 1 ? 'it has' : 'they have'} too little logged to compare.`;
};

/**
 * The whole reading, as something a component can render without deciding
 * anything.
 *
 *   headline  the assessment, in one sentence
 *   lines     the supporting readings, each a complete sentence
 *   focus     the single thing to fix (see `fixFirst`)
 *   caveat    what the above is not standing on
 */
export const buildVerdict = ({ shots }) => {
  const band = shots?.benchmark?.band;
  const headline = verdictHeadline(benchmarkRows(shots), band);
  const focus = fixFirst(shots);

  const lines = [];
  const trend = trendLabel(shots?.trend, band);
  if (trend) lines.push({ key: 'trend', tone: trend.direction, text: `${trend.text}.` });

  // No line about the player's own scores. It compared the player with
  // themselves — a second frame on a card whose rule is one — and Recent
  // Scores, directly beneath, already says it in the same words.

  // A high putting total the fix card has not already explained. Without this
  // the headline calls putting worse and nothing on the card says why.
  if (puttingIsSymptom(shots) && !focus?.link) {
    lines.push({
      key: 'putting',
      text: `Your putting total is high, but your putting stroke is not the problem — ${onGreenFacts(shots.putting)}.`,
    });
  }

  return { headline, lines, focus, caveat: caveatSentence(shots) };
};

/**
 * Is there a verdict at all?
 *
 * A headline needs the benchmark comparison; without one the card would be a
 * confident frame round a trend line, which is the opposite of the point. A
 * player with no shot stats keeps the page they had — the scoring blocks say
 * what a scorecard can say, and nothing here pretends to assess a GAME from
 * eighteen totals.
 */
export const hasVerdict = (verdict) => !!verdict?.headline;
